import React from 'react'
import styled from '@emotion/styled'

import { Typography, Paper, Grid, type Theme } from '@mui/material'

import { InformationItem } from '../InformationItem/InformationItem'
import { formatPhoneNumber, formatBuySellStatus, formatDate, formatTitleCase } from '@edge/spark/utils'

import type { GetCompanyByIdQuery } from '@edge/spark/io-dashboards'
import { useIsMobile } from '@edge/spark/ui-mui'

const StyledHeader = styled(Typography)`
    margin: 0.5rem 0;
`
const TextContainer = styled.div`
    font-size: 14px;
`
const StyledRepLabel = styled.label`
    font-size: 14px;
    color: rgba(0, 0, 0, 0.38);
    padding-right: 6px;
    ${({ theme }) => /* css */ `
    ${(theme as Theme).breakpoints.up('sm')} {
      &:after {
        content: ':';
      }
    }
  `}
`

interface Props {
    data: GetCompanyByIdQuery | undefined
    loading?: boolean
}

export const CompanyInformationWidget: React.FC<Props> = ({ data, loading }: Props) => {
    const mobile = useIsMobile()
    const {
        accountKey,
        establishedAt,
        auctionAccessNumber,
        lastSoldActivity,
        lastPurchaseActivity,
        accountTypeCode,
        phoneMain,
        phoneSecondary,
        email,
        website,
        lastAttended,
        canBuy,
        canSell,
        isActive,
        accountSalesRep,
    } = data?.auction?.accounts?.byId || {}

    const buySellStatus = formatBuySellStatus({ canBuy, canSell })
    const accountStatus = isActive ? `Active ${buySellStatus}` : 'Inactive'

    return (
        <Paper elevation={2} sx={{ p: 2, minHeight: '250px' }}>
            <Grid container spacing={mobile ? 2 : 3} alignItems="flex-start">
                <Grid item container spacing={mobile ? 2 : 3} alignItems="baseline">
                    <Grid item xs={12} sm={6}>
                        <StyledHeader variant="h6">Company Details</StyledHeader>
                    </Grid>
                    <Grid item container sm={6} direction={mobile ? 'column' : 'row'}>
                        <StyledRepLabel>Auction Rep</StyledRepLabel>
                        <TextContainer>{formatTitleCase(accountSalesRep)}</TextContainer>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <InformationItem value={accountKey} label="Account #" loading={loading} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <InformationItem value={accountTypeCode} label="Account Type" loading={loading} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <InformationItem value={formatDate(establishedAt)} label="Customer Since" loading={loading} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <InformationItem value={auctionAccessNumber} label="AuctionACCESS &copy;" loading={loading} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <InformationItem value={accountStatus} label="Status" loading={loading} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <InformationItem
                        value={formatPhoneNumber(phoneMain)}
                        label="Main Phone"
                        loading={loading}
                        href={phoneMain && `tel:${phoneMain}`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <InformationItem
                        value={formatPhoneNumber(phoneSecondary)}
                        label="Secondary Phone"
                        loading={loading}
                        href={phoneSecondary && `tel:${phoneSecondary}`}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <InformationItem
                        value={website?.toLowerCase()}
                        label="Website"
                        loading={loading}
                        href={website && `https://${website}`} // https://stackoverflow.com/a/46903051
                        target="_blank"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <InformationItem
                        value={email?.toLowerCase()}
                        label="Email"
                        loading={loading}
                        href={email && `mailto:${email?.toLowerCase()}`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <InformationItem value={formatDate(lastAttended)} label="Last Attended" loading={loading} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <InformationItem
                        value={formatDate(lastPurchaseActivity)}
                        label="Last Purchased"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <InformationItem value={formatDate(lastSoldActivity)} label="Last Sold" loading={loading} />
                </Grid>
                <Grid item xs={12} sm={6} md={3} />
            </Grid>
        </Paper>
    )
}
